import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchFuncionarioById, updateFuncionario, deleteFuncionario } from '../../services/funcionarioService';

// Função para converter campo boolean que pode vir como buffer do MySQL
const convertBooleanField = (value) => {
  if (value && typeof value === 'object' && value.type === 'Buffer') {
    return value.data[0] === 1;
  } else if (typeof value === 'number') {
    return value === 1;
  } else if (typeof value === 'boolean') {
    return value;
  }
  return false;
};

// Função para converter data para o formato do input date (YYYY-MM-DD)
const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // Se já está no formato correto (YYYY-MM-DD)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      return dateValue.split('T')[0]; // Remove horário se houver
    }
    
    // Converte outros formatos
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    // Retorna no formato YYYY-MM-DD
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.log('Erro ao formatar data para input:', dateValue, error);
    return '';
  }
};

const initialState = {
  nome: '',
  dataNascimento: '',
  cpf: '',
  identidade: '',
  orgaoExpedidor: '',
  email: '',
  telefoneCelular: '',
  telefoneResidencial: '',
  telefoneComercial: '',
  escolaridade: '',
  estadoCivil: '',
  status: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  pais: '',
  estrangeira: false,
  docEstrangeiro: ''
};

const FuncionarioEdit = () => {
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // Estado para erros por campo
  const navigate = useNavigate();

  // Função para obter classes CSS do campo (com erro ou normal)
  const getFieldClass = (fieldName, baseClass = "w-full border rounded px-3 py-2") => {
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-500 focus:ring-red-400 bg-red-50`
      : `${baseClass} focus:outline-none focus:ring-2 focus:ring-green-400`;
  };

  // Limpar mensagens de erro após 8 segundos (apenas erro geral, não fieldErrors)
  useEffect(() => {
    if (error && Object.keys(fieldErrors).length === 0) {
      const timer = setTimeout(() => {
        setError('');
      }, 8000);
      return () => clearTimeout(timer);
    }
    // Se houver fieldErrors, NÃO limpar automaticamente - usuário precisa corrigir
  }, [error, fieldErrors]);

  // Scroll para o topo quando houver mensagens de feedback
  useEffect(() => {
    if (error || success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, success]);

  useEffect(() => {
    const loadFuncionario = async () => {
      try {
        setLoading(true);
        const data = await fetchFuncionarioById(id);
        console.log('Dados do funcionário recebido:', data);
        
        // Converter campos booleanos de buffer para boolean
        const booleanFields = ['estrangeira'];
        booleanFields.forEach(field => {
          if (data[field] !== undefined) {
            data[field] = convertBooleanField(data[field]);
          }
        });
        
        // Formatar a data de nascimento para o input date
        if (data.dataNascimento) {
          data.dataNascimento = formatDateForInput(data.dataNascimento);
        }
        
        setForm(data);
      } catch (err) {
        console.error('Erro ao carregar funcionário:', err);
        setError('Erro ao carregar dados do funcionário.');
      } finally {
        setLoading(false);
      }
    };
    loadFuncionario();
  }, [id]);

  // Funções para aplicar máscaras
  const applyCpfMask = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').slice(0, 14);
  };

  const applyCepMask = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
  };

  const applyPhoneMask = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 10) {
      // Telefone fixo
      if (cleaned.length <= 2) {
        return cleaned.replace(/(\d{1,2})/, '($1');
      } else if (cleaned.length <= 6) {
        return cleaned.replace(/(\d{2})(\d+)/, '($1)$2');
      } else {
        return cleaned.replace(/(\d{2})(\d{4})(\d+)/, '($1)$2-$3');
      }
    } else {
      // Celular
      if (cleaned.length <= 2) {
        return cleaned.replace(/(\d{1,2})/, '($1');
      } else if (cleaned.length <= 7) {
        return cleaned.replace(/(\d{2})(\d+)/, '($1)$2');
      } else {
        return cleaned.replace(/(\d{2})(\d{5})(\d+)/, '($1)$2-$3');
      }
    }
  };

  const handleChangeMasked = (e) => {
    const { name, value } = e.target;
    let maskedValue = value;

    if (name === 'cpf') {
      maskedValue = applyCpfMask(value);
    } else if (name === 'cep') {
      maskedValue = applyCepMask(value);
    } else if (name.includes('telefone') || name.includes('Telefone')) {
      maskedValue = applyPhoneMask(value);
    }

    setForm({ ...form, [name]: maskedValue });
    setError('');
    // Limpar erro específico do campo quando usuário começar a digitar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
    setError('');
    // Limpar erro específico do campo quando usuário começar a digitar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({}); // Limpar erros de campo anteriores
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(''); // Limpar erros anteriores
    try {
      // Preparar dados condicionalmente baseado no campo estrangeira
      const dadosParaEnvio = { ...form };
      
      if (form.estrangeira) {
        // Se é estrangeiro, garantir que CPF seja null
        dadosParaEnvio.cpf = null;
      } else {
        // Se não é estrangeiro, garantir que documento estrangeiro seja null
        dadosParaEnvio.docEstrangeiro = null;
      }
      
      await updateFuncionario(id, dadosParaEnvio);
      setSuccess(true);
      setSuccessMessage(`Funcionário "${form.nome}" foi atualizado com sucesso!`);
      setTimeout(() => navigate('/funcionarios'), 2000);
    } catch (err) {
      console.error('Erro ao atualizar funcionário:', err);
      
      // Processar erros de validação específicos do backend.
      // O middleware devolve em `validationErrors`; ler só `errors` fazia todo
      // erro de campo cair na mensagem genérica.
      const listaDeErros = err.response?.data?.validationErrors || err.response?.data?.errors;

      if (err.response?.status === 400 && Array.isArray(listaDeErros) && listaDeErros.length) {
        const errors = {};
        listaDeErros.forEach(error => {
          const campo = error.path || error.param;
          if (campo && !errors[campo]) errors[campo] = error.msg;
        });
        setFieldErrors(errors);
        setError('Por favor, corrija os campos destacados em vermelho.');
      } else {
        const errorMessage = err.response?.status === 404 
          ? 'Funcionário não encontrado.' 
          : err.response?.status === 403 
          ? 'Você não tem permissão para editar este funcionário.'
          : 'Erro ao atualizar funcionário. Tente novamente.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setError(''); // Limpar erros anteriores
    try {
      await deleteFuncionario(id);
      setDeleteSuccess(true);
      setTimeout(() => navigate('/funcionarios'), 2000);
    } catch (err) {
      console.error('Erro ao excluir funcionário:', err);
      const errorMessage = err.response?.status === 404 
        ? 'Funcionário não encontrado.' 
        : err.response?.status === 403 
        ? 'Você não tem permissão para excluir este funcionário.'
        : err.response?.status === 409
        ? 'Não é possível excluir este funcionário pois existem registros dependentes.'
        : 'Erro ao excluir funcionário. Tente novamente.';
      setError(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
        <span className="text-blue-700 font-medium">Carregando dados do funcionário...</span>
      </div>
    </div>
  );
  
  if (deleteSuccess) return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex items-center justify-center">
        <div className="flex-shrink-0">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-green-800 font-medium">Funcionário excluído com sucesso!</p>
          <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de funcionários...</p>
        </div>
      </div>
    </div>
  );
  
  if (!form) return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center justify-center">
        <div className="flex-shrink-0">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-red-800 font-medium">Funcionário não encontrado</p>
          <p className="text-red-600 text-sm mt-1">O funcionário que você está tentando acessar não existe ou foi removido.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Funcionário</h2>
      
      {/* Mensagens de Feedback */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-red-800 font-medium whitespace-pre-line">{error}</p>
            </div>
            <div className="ml-auto">
              <button 
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-green-800 font-medium">{successMessage || 'Funcionário atualizado com sucesso!'}</p>
              <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de funcionários...</p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Dados Pessoais */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Pessoais</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input type="text" name="nome" value={form.nome} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400" placeholder="Nome completo" />
              {fieldErrors.nome && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nome}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data de Nascimento *</label>
              <input type="date" name="dataNascimento" value={form.dataNascimento} onChange={handleChange} className="w-full border rounded px-3 py-2" />
              {fieldErrors.dataNascimento && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.dataNascimento}</p>
              )}
            </div>
            <div className="flex items-center mt-6">
              <input type="checkbox" name="estrangeira" checked={form.estrangeira} onChange={handleChange} className="mr-2" />
              <label className="text-sm">Estrangeiro</label>
            </div>
            {!form.estrangeira ? (
              <div>
                <label className="block text-sm font-medium mb-1">CPF *</label>
                <input type="text" name="cpf" value={form.cpf} onChange={handleChangeMasked} maxLength={14} className="w-full border rounded px-3 py-2" placeholder="000.000.000-00" />
                {fieldErrors.cpf && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.cpf}</p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">Documento Estrangeiro *</label>
                <input type="text" name="docEstrangeiro" value={form.docEstrangeiro} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="Documento de identificação" />
                {fieldErrors.docEstrangeiro && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.docEstrangeiro}</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Identidade</label>
              <input type="text" name="identidade" value={form.identidade} onChange={handleChange} maxLength={20} className="w-full border rounded px-3 py-2" placeholder="Identidade" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Órgão Expedidor</label>
              <input type="text" name="orgaoExpedidor" value={form.orgaoExpedidor} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Órgão Expedidor" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Email" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Celular</label>
              <input type="text" name="telefoneCelular" value={form.telefoneCelular} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)00000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Residencial</label>
              <input type="text" name="telefoneResidencial" value={form.telefoneResidencial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Comercial</label>
              <input type="text" name="telefoneComercial" value={form.telefoneComercial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Escolaridade</label>
              <select name="escolaridade" value={form.escolaridade} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione a Escolaridade</option>
                <option value="Ensino Fundamental Incompleto">Ensino Fundamental Incompleto</option>
                <option value="Ensino Fundamental Completo">Ensino Fundamental Completo</option>
                <option value="Ensino Médio Incompleto">Ensino Médio Incompleto</option>
                <option value="Ensino Médio Completo">Ensino Médio Completo</option>
                <option value="Ensino Superior Incompleto">Ensino Superior Incompleto</option>
                <option value="Ensino Superior Completo">Ensino Superior Completo</option>
                <option value="Pós-graduação">Pós-graduação</option>
                <option value="Mestrado">Mestrado</option>
                <option value="Doutorado">Doutorado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado Civil</label>
              <select name="estadoCivil" value={form.estadoCivil} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione o Estado Civil</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
                <option value="Separado(a)">Separado(a)</option>
                <option value="União Estável">União Estável</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status *</label>
              <select name="status" value={form.status} onChange={handleChange} className={getFieldClass('status')}>
                <option value="">Selecione o Status</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </select>
              {fieldErrors.status && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.status}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Endereço */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">CEP</label>
              <input type="text" name="cep" value={form.cep} onChange={handleChangeMasked} maxLength={10} className="w-full border rounded px-3 py-2" placeholder="00000-000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Logradouro</label>
              <input type="text" name="logradouro" value={form.logradouro} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Logradouro" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número</label>
              <input type="text" name="numero" value={form.numero} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Número" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Complemento</label>
              <input type="text" name="complemento" value={form.complemento} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="Complemento" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bairro</label>
              <input type="text" name="bairro" value={form.bairro} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="Bairro" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cidade</label>
              <input type="text" name="cidade" value={form.cidade} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Cidade" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <input type="text" name="estado" value={form.estado} onChange={handleChange} maxLength={2} className="w-full border rounded px-3 py-2" placeholder="UF" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input type="text" name="pais" value={form.pais} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="País" />
            </div>
          </div>
        </div>
        
        <div className="flex gap-4 mt-6 justify-end">
          <button
            type="button"
            className="px-4 py-2 rounded bg-gray-400 text-white font-semibold shadow hover:bg-gray-500 focus:outline-none flex items-center gap-2"
            onClick={() => navigate('/funcionarios')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded font-semibold shadow focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2 ${
              loading 
                ? 'bg-green-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            disabled={loading}
           aria-label="Salvar Alterações">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Salvando...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="hidden sm:inline">Salvar Alterações</span>
              </>
            )}
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded font-semibold shadow focus:outline-none flex items-center gap-2 ${
              deleting 
                ? 'bg-red-400 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
            onClick={handleDelete}
            disabled={deleting || loading}
           aria-label="Excluir Funcionário">
            {deleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Excluindo...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} />
                <span className="hidden sm:inline">Excluir Funcionário</span>
              </>
            )}
          </button>
        </div>
      </form>
      
      <ConfirmModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        title="Confirmar Alterações"
        message={`Tem certeza que deseja salvar as alterações do funcionário "${form?.nome}"?`}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o funcionário "${form?.nome}"? Esta ação não pode ser desfeita.`}
        confirmType="delete"
      />
    </div>
  );
};

export default FuncionarioEdit;
